import { IBusinessRowData, UnknownRecord } from './eu-businesses-query';

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  EUR: '€',
};

// "12.3456" -> "+12.3%" or "-50.0" -> "-50%"
const formatPerformance = (value: unknown): string => {
  const n = Number(value);

  if (!Number.isFinite(n)) {
    return '0%';
  }

  const sign = n > 0 ? '+' : '';
  const rounded = Math.round(n * 10) / 10;

  return `${sign}${rounded}%`;
};

// 123456789 -> "£123.5M", 1234 -> "£1.2K", 123 -> "£123"
const formatAmount = (amountInCents: number, currency: string): string => {
  const amount = amountInCents / 100;
  const absoluteAmount = Math.abs(amount);
  const symbol = CURRENCY_SYMBOLS[currency] ?? '';

  const scales = [
    { threshold: 1_000_000_000, suffix: 'B', divisor: 1_000_000_000 },
    { threshold: 1_000_000, suffix: 'M', divisor: 1_000_000 },
    { threshold: 1_000, suffix: 'K', divisor: 1_000 },
  ];

  const scale = scales.find((s) => absoluteAmount >= s.threshold);

  if (scale) {
    const value = absoluteAmount / scale.divisor;
    return `${symbol}${value.toFixed(1)}${scale.suffix}`;
  }

  return `${symbol}${absoluteAmount.toLocaleString()}`;
};

const getValue = <T>(value: unknown, fallback: T): T =>
  (typeof value === typeof fallback ? value : fallback) as T;

export const businessDataFromEURecord = (
  record: UnknownRecord
): IBusinessRowData => {
  const currentValuation = getValue<UnknownRecord>(
    record['current_valuation'],
    {}
  );

  return {
    id: getValue<number>(record['id'], 0),
    name: getValue<string>(record['name'], ''),
    logo: getValue<string>(record['logo_location'], ''),
    performance: formatPerformance(record['performance']),
    valuation: formatAmount(
      getValue<number>(currentValuation['amount_in_cents'], 0),
      getValue<string>(currentValuation['currency'], 'GBP')
    ),
  };
};
